import type { ExoticSelection, ExoticPosition } from "@/features/betslip/types";
import type {
  ExoticToggleInput,
  ExoticSubSelectionInput,
} from "@/features/betslip/builders/types";
import { nCr } from "utilities/number";

/** Maps outcomeType to the finishing position (1-indexed). */
const POSITION_MAP: Record<string, number> = {
  RUNNER_WIN: 1,
  RUNNER_POS_2: 2,
  RUNNER_TOP_2: 2,
  RUNNER_POS_3: 3,
  RUNNER_TOP_3: 3,
  RUNNER_POS_4: 4,
  RUNNER_TOP_4: 4,
};

type GroupedSubSelections = {
  positions: ExoticPosition[][];
  competitors: ExoticSelection["competitors"];
};

const bucketFor = (
  buckets: Map<number, ExoticPosition[]>,
  position: number,
): ExoticPosition[] => {
  const existing = buckets.get(position);
  if (existing) return existing;
  const created: ExoticPosition[] = [];
  buckets.set(position, created);
  return created;
};

/**
 * Group flat subSelections into positional buckets and lift competitors
 * into a per-outcomeId map in one pass. RUNNER_TOP_2 (quinella) runners
 * are duplicated into positions 1 and 2.
 */
const groupSubSelections = (
  subSelections: ExoticSubSelectionInput[],
): GroupedSubSelections => {
  const buckets = new Map<number, ExoticPosition[]>();
  const competitors: ExoticSelection["competitors"] = {};

  for (const sub of subSelections) {
    if (sub.competitor) competitors[sub.outcomeId] = sub.competitor;

    const position = POSITION_MAP[sub.outcomeType ?? ""] ?? 0;
    if (position === 0) continue;

    const entry: ExoticPosition = {
      outcomeId: sub.outcomeId,
      outcomeName: sub.name,
      outcomeType: sub.outcomeType ?? "",
      marketId: sub.marketId,
      marketName: sub.marketName ?? "",
      runnerNumber: sub.attributes?.runnerNumber,
    };

    if (sub.outcomeType === "RUNNER_TOP_2") {
      bucketFor(buckets, 1).push({ ...entry });
      bucketFor(buckets, 2).push({ ...entry });
    } else {
      bucketFor(buckets, position).push(entry);
    }
  }

  // Runner-number order within each position, like legacy
  // ExoticRunnersInPosition — sorted once here so consumers render the
  // stored order directly.
  const positions = [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, runners]) =>
      [...runners].sort(
        (a, b) =>
          parseInt(a.runnerNumber ?? "0", 10) -
          parseInt(b.runnerNumber ?? "0", 10),
      ),
    );
  return { positions, competitors };
};

/**
 * Count valid ordered permutations via recursive backtracking. Dedup is
 * keyed on `runnerNumber` because a single runner appears as different
 * outcomeIds at different positions, and `outcomeName` is not always
 * populated (the complex-bet-builder converter hard-codes a placeholder
 * string for exotic subSelections). `runnerNumber` comes from the
 * outcome's `attributes.runnerNumber` and is stable across positions.
 */
const countOrderedCombinations = (positions: ExoticPosition[][]): number => {
  let count = 0;

  const identityOf = (runner: ExoticPosition): string =>
    runner.runnerNumber ?? runner.outcomeName;

  const backtrack = (posIdx: number, usedRunners: Set<string>) => {
    const runners = positions[posIdx];
    if (!runners) {
      count++;
      return;
    }
    for (const runner of runners) {
      const id = identityOf(runner);
      if (usedRunners.has(id)) continue;
      usedRunners.add(id);
      backtrack(posIdx + 1, usedRunners);
      usedRunners.delete(id);
    }
  };

  backtrack(0, new Set());
  return count;
};

/** Quinella: count unique unordered pairs. nCr(N, 2). */
const countQuinellaCombinations = (positions: ExoticPosition[][]): number => {
  const [firstPosition, secondPosition] = positions;
  if (!firstPosition || !secondPosition) return 0;
  return nCr(firstPosition.length, 2);
};

const isQuinella = (marketName: string): boolean =>
  marketName.toLowerCase() === "quinella";

export const buildExoticSelection = (
  input: ExoticToggleInput,
): ExoticSelection => {
  const { event, market, hub, outcomeId, subSelections, originType, originId } =
    input;

  const { positions, competitors } = groupSubSelections(subSelections);
  const firstMarketName = subSelections[0]?.marketName ?? "";

  const combinations = isQuinella(firstMarketName)
    ? countQuinellaCombinations(positions)
    : countOrderedCombinations(positions);

  return {
    kind: "exotic",
    outcomeId,
    eventId: event.id,
    marketId: market.id,
    eventName: event.eventName ?? "",
    marketName: firstMarketName,
    sport: event.sport ?? "",
    startTime: market.nextBetStop ?? event.scheduledStartTime ?? new Date(),
    hub,
    marketType: market.marketType ?? "",
    odds: 0,
    isSP: false,
    isMultiable: false,
    independentPerEvent: market.independentPerEvent ?? false,
    filters: event.filters ?? [],
    blockPromotionsBets: !!market.blockPromotionsBets,
    hasPromotionalMarkets: !!market.hasPromotionalMarkets,
    hasLiveMarkets: false,
    originType: originType ?? "BETTING",
    originId,
    positions,
    combinations,
    competitors,
    marketStatus: market.status,
    eventStatus: event.status,
    // Racing exotics don't carry a stream or a single match-page URL.
    streamUri: undefined,
    eventUrl: undefined,
    expectedResults: event.expectedResults,
    meetingId: event.meetingId,
  };
};
